/**
 * WINIX INDIA - FIND MY FIT DYNAMIC QUIZ CONTROLLER
 * Supports dynamic Quiz Steps, custom feature blocks, and intelligent matching
 */

(function () {
  'use strict';

  class FindMyFitSection {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.dataEl = container.querySelector('.fmf-products-data');
      
      try {
        this.products = this.dataEl ? JSON.parse(this.dataEl.textContent) : [];
      } catch (e) {
        console.error('FindMyFit: Failed to parse products data', e);
        this.products = [];
      }

      this.stepCards = Array.from(container.querySelectorAll('.find-my-fit__step-content'));
      this.resultsStep = container.querySelector('.find-my-fit__results-step');
      this.progressBar = container.querySelector('.find-my-fit__progress-fill');
      this.progressPct = container.querySelector('.find-my-fit__progress-pct');
      this.progressStepName = container.querySelector('.find-my-fit__progress-step-name');
      this.dotsContainer = container.querySelector('.find-my-fit__step-dots');
      
      this.btnPrev = container.querySelector('.fmf-btn-prev');
      this.btnNext = container.querySelector('.fmf-btn-next');
      this.btnSkip = container.querySelector('.fmf-btn-skip');
      this.btnRetake = container.querySelector('.fmf-btn-retake');

      this.productsGrid = container.querySelector('.find-my-fit__products-grid');
      this.chipsContainer = container.querySelector('.find-my-fit__results-summary-chips');

      // Dynamic State
      this.currentStepIndex = 0;
      this.totalSteps = this.stepCards.length;
      this.userAnswers = {}; // key -> Array of selected values
      this.userLabels = {};  // key -> Array of readable labels

      this.init();
    }

    init() {
      if (this.totalSteps === 0) return;

      this.bindEvents();
      this.updateUI();
    }

    bindEvents() {
      // Option button clicks
      this.container.addEventListener('click', (e) => {
        const optionBtn = e.target.closest('.find-my-fit__option-item');
        if (optionBtn) {
          this.handleOptionClick(optionBtn);
        }
      });

      // Navigation buttons
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => this.goToPrevStep());
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => this.goToNextStep());
      }
      if (this.btnSkip) {
        this.btnSkip.addEventListener('click', () => this.goToNextStep(true));
      }
      if (this.btnRetake) {
        this.btnRetake.addEventListener('click', () => this.resetQuiz());
      }

      // Add to cart delegation
      this.container.addEventListener('click', (e) => {
        const atcBtn = e.target.closest('.fmf-btn-cart');
        if (atcBtn) {
          e.preventDefault();
          this.handleAddToCart(atcBtn);
        }
      });
    }

    handleOptionClick(optionBtn) {
      const stepEl = optionBtn.closest('.find-my-fit__step-content');
      if (!stepEl) return;

      const questionKey = (stepEl.dataset.questionKey || 'general').trim().toLowerCase();
      const optionValue = (optionBtn.dataset.value || '').trim().toLowerCase();
      const optionTitleEl = optionBtn.querySelector('.find-my-fit__option-title');
      const optionLabel = optionTitleEl ? optionTitleEl.textContent.trim() : optionValue;
      const isMulti = stepEl.dataset.multiselect === 'true';

      if (!this.userAnswers[questionKey]) {
        this.userAnswers[questionKey] = [];
        this.userLabels[questionKey] = [];
      }

      if (isMulti) {
        // Multi-select toggle
        optionBtn.classList.toggle('selected');
        const selectedItems = Array.from(stepEl.querySelectorAll('.find-my-fit__option-item.selected'));
        this.userAnswers[questionKey] = selectedItems.map(el => (el.dataset.value || '').trim().toLowerCase());
        this.userLabels[questionKey] = selectedItems.map(el => {
          const t = el.querySelector('.find-my-fit__option-title');
          return t ? t.textContent.trim() : el.dataset.value;
        });
      } else {
        // Single select
        stepEl.querySelectorAll('.find-my-fit__option-item').forEach(el => el.classList.remove('selected'));
        optionBtn.classList.add('selected');
        this.userAnswers[questionKey] = [optionValue];
        this.userLabels[questionKey] = [optionLabel];

        // Smooth auto-advance after 260ms
        setTimeout(() => {
          if (this.currentStepIndex < this.totalSteps) {
            this.goToNextStep();
          }
        }, 260);
      }

      this.updateNavButtons();
    }

    goToNextStep(isSkip = false) {
      if (this.currentStepIndex < this.totalSteps - 1) {
        this.currentStepIndex++;
        this.updateUI();
        this.scrollToWizardTop();
      } else if (this.currentStepIndex === this.totalSteps - 1) {
        // Last step finished -> show results!
        this.currentStepIndex = this.totalSteps;
        this.renderResults();
        this.updateUI();
        this.scrollToWizardTop();
      }
    }

    goToPrevStep() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
        this.updateUI();
        this.scrollToWizardTop();
      }
    }

    resetQuiz() {
      this.currentStepIndex = 0;
      this.userAnswers = {};
      this.userLabels = {};

      // Deselect all cards
      this.container.querySelectorAll('.find-my-fit__option-item').forEach(el => el.classList.remove('selected'));
      this.updateUI();
      this.scrollToWizardTop();
    }

    scrollToWizardTop() {
      const rect = this.container.getBoundingClientRect();
      if (rect.top < 0 || rect.top > 300) {
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    updateUI() {
      const isResults = this.currentStepIndex >= this.totalSteps;

      // Handle step visibility
      this.stepCards.forEach((step, idx) => {
        if (idx === this.currentStepIndex && !isResults) {
          step.classList.add('active');
        } else {
          step.classList.remove('active');
        }
      });

      if (this.resultsStep) {
        if (isResults) {
          this.resultsStep.classList.add('active');
        } else {
          this.resultsStep.classList.remove('active');
        }
      }

      // Progress bar and navigation footer
      const progressWrapper = this.container.querySelector('.find-my-fit__progress-wrapper');
      const footerNav = this.container.querySelector('.find-my-fit__footer');

      if (isResults) {
        if (progressWrapper) progressWrapper.style.display = 'none';
        if (footerNav) footerNav.style.display = 'none';
      } else {
        if (progressWrapper) progressWrapper.style.display = 'block';
        if (footerNav) footerNav.style.display = 'flex';

        const stepNum = this.currentStepIndex + 1;
        const pct = Math.round((stepNum / this.totalSteps) * 100);

        if (this.progressBar) {
          this.progressBar.style.width = `${pct}%`;
        }
        if (this.progressPct) {
          this.progressPct.textContent = `${pct}% Complete`;
        }

        const activeStepEl = this.stepCards[this.currentStepIndex];
        const stepName = activeStepEl ? activeStepEl.dataset.stepName || `Step ${stepNum}` : `Step ${stepNum}`;
        if (this.progressStepName) {
          this.progressStepName.innerHTML = `<span>Question ${stepNum} of ${this.totalSteps}</span> &bull; <span>${stepName}</span>`;
        }

        // Dots
        if (this.dotsContainer) {
          const dots = Array.from(this.dotsContainer.children);
          dots.forEach((dot, idx) => {
            dot.className = 'find-my-fit__step-dot';
            if (idx === this.currentStepIndex) {
              dot.classList.add('active');
            } else if (idx < this.currentStepIndex) {
              dot.classList.add('completed');
            }
          });
        }

        this.updateNavButtons();
      }
    }

    updateNavButtons() {
      if (!this.btnPrev || !this.btnNext) return;

      this.btnPrev.disabled = this.currentStepIndex === 0;

      if (this.currentStepIndex === this.totalSteps - 1) {
        this.btnNext.innerHTML = `See My Matches <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;
      } else {
        this.btnNext.innerHTML = `Next Step <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>`;
      }
    }

    renderResults() {
      if (!this.productsGrid) return;

      // Render summary chips
      if (this.chipsContainer) {
        this.renderSummaryChips();
      }

      // Calculate matches
      const scoredProducts = this.calculateMatchScores();

      if (scoredProducts.length === 0) {
        this.productsGrid.innerHTML = `
          <div class="find-my-fit__empty-state">
            <div class="find-my-fit__empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/></svg>
            </div>
            <div class="find-my-fit__empty-title">No direct product match found</div>
            <div class="find-my-fit__empty-desc">Try choosing broader room size or air quality preferences, or speak with our clean air specialists.</div>
            <button type="button" class="find-my-fit__retake-btn fmf-btn-retake">Restart Quiz</button>
          </div>
        `;
        return;
      }

      // Render Cards
      let html = '';
      scoredProducts.forEach((item, index) => {
        const isTopMatch = index === 0;
        const product = item.data;
        const matchPct = item.matchPercentage;
        const whyBullets = item.whyBullets;

        const compareAtPrice = product.compare_at_price ? product.compare_at_price_formatted : null;
        const price = product.price_formatted || (product.price ? `₹${(product.price / 100).toLocaleString('en-IN')}` : '');
        const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;

        let discountTag = '';
        if (hasDiscount) {
          const savings = Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100);
          discountTag = `<span class="find-my-fit__discount-badge">${savings}% OFF</span>`;
        }

        html += `
          <div class="find-my-fit__product-card ${isTopMatch ? 'top-match' : ''}">
            ${isTopMatch ? `
              <div class="find-my-fit__top-banner">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Top Recommended Fit
              </div>
            ` : ''}

            <div class="find-my-fit__card-top">
              <span class="find-my-fit__match-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                ${matchPct}% Match
              </span>
              ${product.badge_text ? `
                <span class="find-my-fit__custom-badge"${product.badge_bg && product.badge_bg !== '#0284c7' ? ` style="background-color: ${product.badge_bg}; color: ${product.badge_color || '#ffffff'};"` : ''}>
                  ${this.escapeHtml(product.badge_text)}
                </span>
              ` : ''}
            </div>

            <div class="find-my-fit__image-wrapper">
              <a href="${product.url}" aria-label="${this.escapeHtml(product.title)}">
                <img src="${product.image || 'https://via.placeholder.com/400x400?text=Winix+Product'}" alt="${this.escapeHtml(product.title)}" loading="lazy" />
              </a>
            </div>

            <h3 class="find-my-fit__card-title">
              <a href="${product.url}">${this.escapeHtml(product.title)}</a>
            </h3>

            <div class="find-my-fit__price-row">
              <span class="find-my-fit__price">${price}</span>
              ${compareAtPrice ? `<span class="find-my-fit__compare-price">${compareAtPrice}</span>` : ''}
              ${discountTag}
            </div>

            <div class="find-my-fit__specs-chips">
              ${product.coverage_area ? `
                <span class="find-my-fit__spec-pill coverage">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h18v18H3z"/></svg>
                  ${this.escapeHtml(product.coverage_area)}
                </span>
              ` : ''}
              ${product.smart_connectivity === 'yes' ? `
                <span class="find-my-fit__spec-pill wifi">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12.55a11 11 0 0 1 14.08 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"/></svg>
                  Winix Smart App
                </span>
              ` : ''}
              <span class="find-my-fit__spec-pill">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                True HEPA 99.97%
              </span>
            </div>

            <div class="find-my-fit__why-fit-box">
              <div class="find-my-fit__why-fit-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                Why this fits your specifications:
              </div>
              <ul class="find-my-fit__why-fit-list">
                ${whyBullets.map(bullet => `
                  <li>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
                    <span>${bullet}</span>
                  </li>
                `).join('')}
              </ul>
            </div>

            <div class="find-my-fit__card-actions">
              ${product.variant_id && product.available ? `
                <button type="button" class="find-my-fit__btn-cart fmf-btn-cart" data-variant-id="${product.variant_id}">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                  <span>Add to Cart</span>
                </button>
              ` : `
                <a href="${product.url}" class="find-my-fit__btn-cart" style="background: #334155;">
                  <span>${product.available === false ? 'Sold Out - View Specs' : 'View Product'}</span>
                </a>
              `}

              <a href="${product.url}" class="find-my-fit__btn-details">
                <span>Explore Full Details</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
            </div>
          </div>
        `;
      });

      this.productsGrid.innerHTML = html;
    }

    renderSummaryChips() {
      const chips = [];
      Object.keys(this.userLabels).forEach(key => {
        const labels = this.userLabels[key] || [];
        labels.forEach(lbl => {
          if (lbl && !chips.includes(lbl)) {
            chips.push(lbl);
          }
        });
      });

      this.chipsContainer.innerHTML = chips.map(chip => `
        <span class="find-my-fit__chip">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          ${this.escapeHtml(chip)}
        </span>
      `).join('');
    }

    calculateMatchScores() {
      if (!this.products || this.products.length === 0) return [];

      const answeredKeys = Object.keys(this.userAnswers).filter(k => (this.userAnswers[k] || []).length > 0);
      const totalAnswered = Math.max(1, answeredKeys.length);
      const pointsPerKey = Math.round(75 / totalAnswered);

      const userRooms = (this.userAnswers['room_types'] || []).map(s => s.toLowerCase());
      const userSizes = (this.userAnswers['room_sizes'] || []).map(s => s.toLowerCase());
      const userConcerns = (this.userAnswers['primary_concerns'] || []).map(s => s.toLowerCase());
      const userSmart = (this.userAnswers['smart_connectivity'] || []).map(s => s.toLowerCase());
      const userFeatures = (this.userAnswers['features'] || []).map(s => s.toLowerCase());

      const scored = this.products.map(product => {
        let score = 0;
        const whyBullets = [];

        // Pre-compile product keyword arrays
        const pRoomTypes = (product.room_types || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        const pRoomSizes = (product.room_sizes || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        const pConcerns = (product.primary_concerns || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        const pFeatures = (product.features || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        const pCustom = (product.custom_tags || '').toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
        const allKeywords = [...pRoomTypes, ...pRoomSizes, ...pConcerns, ...pFeatures, ...pCustom];

        let roomMatched = false;
        let sizeMatched = false;

        // 1. Check Room Types
        if (userRooms.length > 0) {
          roomMatched = userRooms.some(uR => pRoomTypes.some(pR => pR === uR || pR.includes(uR) || uR.includes(pR)));
          if (roomMatched) {
            score += pointsPerKey;
            if (userRooms.includes('bedroom') || userRooms.includes('bed')) {
              whyBullets.push('Whisper-quiet sleep mode (25-27dB) ideal for undisturbed bedroom nights.');
            } else if (userRooms.includes('living') || userRooms.includes('living_room')) {
              whyBullets.push('High CADR air circulation engineered for active living rooms and family spaces.');
            } else if (userRooms.includes('kitchen')) {
              whyBullets.push('Swiftly clears pungent cooking smoke, grease, and spice aromas.');
            } else if (userRooms.includes('desk') || userRooms.includes('work_desk') || userRooms.includes('office')) {
              whyBullets.push('Compact personal desktop footprint providing a dedicated clean air zone.');
            } else if (userRooms.includes('large_hall') || userRooms.includes('hall')) {
              whyBullets.push('Heavy-duty maximum CADR output engineered for expansive open halls and duplexes.');
            }
          }
        }

        // 2. Check Room Sizes
        if (userSizes.length > 0) {
          sizeMatched = userSizes.some(uS => pRoomSizes.some(pS => pS === uS || pS.includes(uS) || uS.includes(pS)));
          if (sizeMatched) {
            score += pointsPerKey;
            if (product.coverage_area) {
              whyBullets.push(`Delivers 4-5 complete air cleanings every hour across ${product.coverage_area}.`);
            }
          }
        }

        // Synergy: Matches BOTH Room Type & Room Size!
        if (roomMatched && sizeMatched) {
          score += 25; // High synergy bonus
        }

        // 3. Check Smart Connectivity
        if (userSmart.length > 0) {
          if (userSmart.includes('smart_yes')) {
            if (product.smart_connectivity === 'yes' || pFeatures.includes('wifi') || pFeatures.includes('smart_app')) {
              score += pointsPerKey + 5;
              whyBullets.push('Winix Smart App & WiFi enabled for remote control and live indoor AQI tracking.');
            }
          } else if (userSmart.includes('smart_no')) {
            if (product.smart_connectivity === 'no' || !pFeatures.includes('wifi')) {
              score += pointsPerKey + 5;
              whyBullets.push('Simple, dependable one-touch push-button control with zero setup needed.');
            }
          } else {
            // "any"
            score += pointsPerKey;
          }
        }

        // 4. Check Air Concerns
        if (userConcerns.length > 0) {
          const concernHits = userConcerns.filter(uC => pConcerns.some(pC => pC === uC || pC.includes(uC) || uC.includes(pC)));
          if (concernHits.length > 0) {
            score += pointsPerKey;
            if (userConcerns.includes('dust_pollution') || userConcerns.includes('smog')) {
              whyBullets.push('Hospital-grade True HEPA 99.97% actively eliminates severe PM2.5 and winter smog.');
            } else if (userConcerns.includes('allergies_asthma') || userConcerns.includes('lungs')) {
              whyBullets.push('Captures 99.97% of airborne pollen, mold spores & microscopic asthma triggers.');
            } else if (userConcerns.includes('pet_hair') || userConcerns.includes('pets')) {
              whyBullets.push('High-capacity pre-filter captures pet fur, dander, and neutralizes pet odors.');
            } else if (userConcerns.includes('smoke_odors') || userConcerns.includes('cooking')) {
              whyBullets.push('Activated carbon bed rapidly absorbs VOC gases, tobacco smoke & cooking odors.');
            }
          }
        }

        // 5. Check Features
        if (userFeatures.length > 0) {
          const featureHits = userFeatures.filter(uF => pFeatures.some(pF => pF === uF || pF.includes(uF) || uF.includes(pF)));
          if (featureHits.length > 0) {
            score += pointsPerKey;
            if (userFeatures.includes('plasmawave') && pFeatures.includes('plasmawave')) {
              whyBullets.push('PlasmaWave® Technology neutralizes airborne viruses and bacteria at the molecular level.');
            }
            if (userFeatures.includes('smart_sensor') && pFeatures.includes('smart_sensor')) {
              whyBullets.push('Smart sensor continuously monitors indoor air quality and adjusts fan speed automatically.');
            }
          }
        }

        // Handle other custom answered keys
        answeredKeys.forEach(k => {
          if (!['room_types', 'room_sizes', 'primary_concerns', 'smart_connectivity', 'features'].includes(k)) {
            const vals = this.userAnswers[k] || [];
            if (vals.some(v => allKeywords.includes(v))) {
              score += pointsPerKey;
            }
          }
        });

        // Add merchant priority score (1 to 10)
        const priority = parseInt(product.priority_score, 10) || 5;
        score += priority;

        // Custom pitch if defined in product block
        if (product.why_fit_text && product.why_fit_text.trim() !== '') {
          whyBullets.unshift(this.escapeHtml(product.why_fit_text.trim()));
        }

        // Fallback bullets if needed
        if (whyBullets.length < 2) {
          if (product.coverage_area) {
            whyBullets.push(`Coverage rating: ${product.coverage_area}`);
          }
          whyBullets.push('True HEPA 99.97% filtration removes microscopic dust, allergens and pollutants.');
        }

        // Normalized score
        const maxPossible = (pointsPerKey * totalAnswered) + 25 + 10;
        const normalizedPct = Math.min(99, Math.max(76, Math.round((score / maxPossible) * 100)));

        return {
          data: product,
          score: score,
          matchPercentage: normalizedPct,
          whyBullets: whyBullets.slice(0, 3)
        };
      });

      // Sort descending by score
      scored.sort((a, b) => b.score - a.score);

      // Ensure top match is high and confident (98%)
      if (scored.length > 0 && scored[0].matchPercentage < 96) {
        scored[0].matchPercentage = 98;
      }

      return scored;
    }

    handleAddToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const originalText = btn.innerHTML;
      btn.classList.add('loading');
      btn.disabled = true;
      btn.innerHTML = `
        <svg class="fmf-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="animation: fmfSpin 0.7s linear infinite;"><circle cx="12" cy="12" r="10" stroke-opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/></svg>
        <span>Adding to Cart...</span>
      `;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
        return response.json();
      })
      .then(item => {
        btn.classList.remove('loading');
        btn.style.backgroundColor = '#16a34a';
        btn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
          <span>Added to Cart!</span>
        `;

        // Notify theme and cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { item } }));

        // Update cart counters across the store
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-count]').forEach(el => {
              el.textContent = cart.item_count;
            });

            // Trigger theme cart drawer if present
            const cartDrawer = document.querySelector('cart-drawer, .minicart__wrapper, #cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              const cartIcon = document.querySelector('.header__icon--cart, [data-action="open-cart"]');
              if (cartIcon) cartIcon.click();
            }
          })
          .catch(() => {});

        setTimeout(() => {
          btn.disabled = false;
          btn.style.backgroundColor = '';
          btn.innerHTML = originalText;
        }, 2500);
      })
      .catch(err => {
        console.error('FindMyFit: Add to cart error', err);
        btn.classList.remove('loading');
        btn.disabled = false;
        btn.innerHTML = originalText;
        window.location.href = `/cart/add?id=${variantId}&quantity=1`;
      });
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  // Animation keyframe for spinner
  if (!document.getElementById('fmf-spinner-keyframes')) {
    const styleEl = document.createElement('style');
    styleEl.id = 'fmf-spinner-keyframes';
    styleEl.textContent = `@keyframes fmfSpin { 100% { transform: rotate(360deg); } }`;
    document.head.appendChild(styleEl);
  }

  // Initialize all Find My Fit sections on the page
  function initFindMyFitSections() {
    document.querySelectorAll('.sec__find-my-fit').forEach(sectionEl => {
      if (!sectionEl._fmfInitialized) {
        sectionEl._fmfInitialized = true;
        new FindMyFitSection(sectionEl);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFindMyFitSections);
  } else {
    initFindMyFitSections();
  }

  // Support Shopify Theme Editor re-rendering
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.sec__find-my-fit') || (e.target.classList.contains('sec__find-my-fit') ? e.target : null);
    if (section) {
      section._fmfInitialized = false;
      new FindMyFitSection(section);
    }
  });

})();
